/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Images } from "@packages/sdk";
import type { JSX, ReactNode } from "react";
import { forwardRef } from "react";

import {
  colors,
  constantSemanticColors,
  radius,
  spacing,
  spacingMasks,
  zIndices,
} from "../../../../global/emotion";
import { stylesOpacity } from "../../../../global/emotion/styles/stylesOpacity";
import { stylesOutline } from "../../../../global/emotion/styles/stylesOutline";
import { stylesScale } from "../../../../global/emotion/styles/stylesScale";
import { useThemeValues } from "../../../hooks/useThemeValues";
import type { HallowElement } from "../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../types/hallowElementProps.types";
import type { KeyedElement } from "../../../types/keyedElement.types";
import type { WithAsChild } from "../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../utils/determineElementFromAsChild";
import type { MaskProps } from "../../_base/_core/Mask/Mask.emotion";
import { Mask } from "../../_base/_core/Mask/Mask.emotion";
import { Tag } from "../../_base/_core/Tag/Tag.emotion";
import type { TextProps } from "../../_base/_core/Text/Text.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { SubscriptionIcon } from "../../_base/icons/Icon/GeneralIcons";
import { HallowImage } from "../../blocks/other/HallowImage/HallowImage";

const hallowElement: HallowElement = "div";

const centerActionsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  left: "50%",
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
};
const containerStyle: Interpolation<{ color: MaskProps["color"] }> = (
  props,
) => ({
  WebkitBackfaceVisibility: "hidden",
  backgroundColor: props.color,
  borderRadius: spacing.m,
  display: "flex",
  flexDirection: "column",
  height: spacing.full,
  position: "absolute",
  top: spacing.none,
  width: spacing.full,
});
const descriptionStyle: Interpolation = {
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  color: constantSemanticColors.onColorHighest,
  display: "-webkit-box",
  overflow: "hidden",
};
const determinedElementStyle: Interpolation = {
  borderRadius: radius.m,
  display: "block",
  paddingBottom: "60px",
  paddingTop: spacing.full,
  position: "relative",
  width: spacing.full,
};
const imgStyle: Interpolation = {
  WebkitUserDrag: "none",
  borderTopLeftRadius: radius.m,
  borderTopRightRadius: radius.m,
  height: spacing.full,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  userSelect: "none",
  width: spacing.full,
};
const imgBlurStyle: Interpolation = {
  filter: `blur(${spacing.ml})`,
  transform: "scale(1.1)",
};
const imgContainerStyle: Interpolation = {
  borderTopLeftRadius: radius.m,
  borderTopRightRadius: radius.m,
  flexGrow: 1,
  maxHeight: "calc(100% - 60px)",
  position: "relative",
};
const imgContainerBlurStyle: Interpolation = {
  overflow: "hidden",
};
const infoContainerStyle: Interpolation = {
  alignItems: "end",
  borderBottomLeftRadius: radius.m,
  borderBottomRightRadius: radius.m,
  display: "flex",
  gap: spacing.s,
  marginTop: "-1px",
  paddingBottom: spacing.l,
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  paddingTop: spacing.xxs,
};
const infoContainerLeftContainerStyle: Interpolation = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: spacing.s,
  height: spacing.full,
  minWidth: 0,
};
const maskContainerStyle: Interpolation = {
  bottom: spacing.none,
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  height: spacingMasks.m,
  justifyContent: "flex-end",
  paddingBottom: spacing.none,
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
  position: "absolute",
  width: spacing.full,
  zIndex: zIndices.absolute,
};
const maskStyle: Interpolation = {
  bottom: spacing.none,
  left: spacing.none,
  position: "absolute",
  right: spacing.none,
  top: spacing.none,
};
const rightActionsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
};
const tagStyle: Interpolation = {
  width: "fit-content !important",
  zIndex: 0,
};
const tagWidthFitStyle: Interpolation = {
  width: "fit-content !important",
};
const tagsContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
};
const titleStyle: Interpolation = {
  flex: 1,
  overflowX: "hidden",
  overflowY: "visible",
  position: "relative",
};
const titleContainerStyle: Interpolation = {
  alignItems: "center",
  color: constantSemanticColors.onColorHighest,
  display: "flex",
  gap: spacing.xxs,
  zIndex: zIndices.absolute,
};

export type CardProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  activeState?: boolean;
  alternativeTitle?: string;
  centerActions?: KeyedElement[];
  color?: MaskProps["color"] | null;
  description?: ReactNode;
  descriptionCSS?: Interpolation;
  descriptionProps?: TextProps;
  focusState?: boolean;
  hoverState?: boolean;
  imgBlur?: boolean;
  imgContainerProps?: HallowElementProps<"div">;
  imgContainerCSS?: Interpolation;
  imgSrc: Images;
  imgCSS?: Interpolation;
  infoContainerCSS?: Interpolation;
  maskContainerCSS?: Interpolation;
  maskCSS?: Interpolation;
  maskProps?: MaskProps;
  rightActions?: KeyedElement[];
  subscription?: boolean;
  tag?: string;
  tags?: ReactNode[];
  title?: string;
  titleCSS?: Interpolation;
  titleProps?: TextProps;
};

export const Card = forwardRef<any, CardProps>(
  (
    {
      activeState = true,
      alternativeTitle,
      asChild = false,
      centerActions,
      children,
      color,
      description,
      descriptionCSS = {},
      descriptionProps,
      focusState = true,
      hoverState = true,
      imgBlur,
      imgContainerProps,
      imgContainerCSS = {},
      imgSrc,
      imgCSS = {},
      infoContainerCSS = {},
      maskProps,
      maskCSS = {},
      maskContainerCSS = {},
      rightActions,
      subscription,
      tag,
      tags,
      title,
      titleCSS = {},
      titleProps,
      ...props
    },
    ref,
  ) => {
    const theme = useThemeValues();
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const colorWithFallback = color ?? "black";

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        <div
          key="container"
          css={containerStyle({
            color:
              theme?.semanticColors?.[colorWithFallback] ??
              colors[colorWithFallback] ??
              colorWithFallback,
          })}
        >
          <div
            css={[
              imgContainerStyle,
              imgBlur && imgContainerBlurStyle,
              imgContainerCSS,
            ]}
            {...imgContainerProps}
          >
            {imgContainerProps?.children}
            {imgSrc && (
              <HallowImage
                alt={title}
                src={imgSrc}
                size={"m"}
                css={[imgStyle, imgBlur && imgBlurStyle, imgCSS]}
              />
            )}
            <div css={[maskContainerStyle, maskContainerCSS]}>
              <Mask
                color={colorWithFallback}
                direction="to top"
                size="m"
                css={[maskStyle, maskCSS]}
                {...maskProps}
              />
              {tag && (
                <Tag css={tagStyle} variant="lighten">
                  {tag}
                </Tag>
              )}
              {title && (
                <div css={titleContainerStyle}>
                  {subscription && <SubscriptionIcon.Filled />}
                  <Text
                    css={[titleStyle, titleCSS]}
                    {...({
                      overflow: "ellipsis",
                      size: "l",
                      type: "title",
                      as: "h3",
                      ...titleProps,
                    } as TextProps)}
                    data-content={title}
                  >
                    {title}
                  </Text>
                </div>
              )}
            </div>
            {centerActions?.length > 0 && (
              <div css={centerActionsStyle}>{centerActions}</div>
            )}
          </div>
          <div css={[infoContainerStyle, infoContainerCSS]}>
            <div css={infoContainerLeftContainerStyle}>
              {alternativeTitle && (
                <div css={titleContainerStyle}>
                  {subscription && <SubscriptionIcon.Filled />}
                  <Text
                    css={titleStyle}
                    {...({
                      overflow: "ellipsis",
                      size: "l",
                      type: "title",
                      as: "h3",
                      ...titleProps,
                    } as TextProps)}
                    data-content={title}
                  >
                    {alternativeTitle}
                  </Text>
                </div>
              )}
              {description && (
                <Text
                  css={[descriptionStyle, descriptionCSS]}
                  {...({
                    size: "s",
                    type: "body",
                    ...descriptionProps,
                  } as TextProps)}
                >
                  {description}
                </Text>
              )}
              {tags?.length > 0 && (
                <div css={tagsContainerStyle}>
                  {tags?.map((tag, index) => (
                    <Tag
                      key={`${tag}${index}`}
                      css={tagWidthFitStyle}
                      variant="lighten"
                    >
                      {tag}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
            {rightActions?.length > 0 && (
              <div css={rightActionsStyle}>{rightActions}</div>
            )}
          </div>
        </div>,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        css={[
          determinedElementStyle,
          activeState ? stylesOpacity.active : null,
          activeState ? stylesOpacity.base : null,
          stylesOutline.base,
          focusState ? stylesOutline.focus : null,
          hoverState ? stylesOutline.hover : null,
          activeState && hoverState ? stylesScale.base : null,
          activeState && hoverState ? stylesScale.m : null,
        ]}
        {...props}
      >
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

Card.displayName = "Card";
